import React, { useState, useRef, useLayoutEffect } from 'react'
import { Document, Page } from 'react-pdf'

import { Box, Button, ButtonGroup, Paper, Skeleton } from '@mui/material'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

import '../../styles/pdfViewer.css'

const LoadingPage = ({ height }) => <Skeleton variant='rect' width='100%' height={height} />

const LoadingDocument = ({ height }) => (
  <Paper square>
    <LoadingPage height={height} />
  </Paper>
)

const PageCounter = ({ pageNumber, onPageNumberChange, numPages }) => (
  <Box display='flex' width='100%' justifyContent='center' alignItems='center' my={2}>
    <ButtonGroup size='small'>
      <Button
        disabled={pageNumber <= 1}
        onClick={() => onPageNumberChange(Math.max(1, pageNumber - 1))}
      >
        <NavigateBeforeIcon />
      </Button>
      <Button
        style={{ textTransform: 'none', cursor: 'default' }}
        disableRipple
        disableTouchRipple
        disableFocusRipple
      >
        Page {pageNumber} of {numPages}
      </Button>
      <Button
        disabled={pageNumber >= numPages}
        onClick={() => onPageNumberChange(Math.min(pageNumber + 1, numPages))}
      >
        <NavigateNextIcon />
      </Button>
    </ButtonGroup>
  </Box>
)

export const PDFPreview = ({ file, width = 64 }) => (
  <Document file={file}>
    <Box width={width}>
      <Page pageNumber={1} width={width} />
    </Box>
  </Document>
)

const PDFViewer = ({ file, width = 1280 }) => {
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [ratioHeight, setRatioHeight] = useState(Math.SQRT2)
  const [pageWidth, setPageWidth] = useState(width)
  const [pageHeight, setPageHeight] = useState(width * (ratioHeight ?? Math.SQRT2))
  const pageRef = useRef(null)

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const onPageRenderSuccess = ({ width: pageWidth, height: pageHeight }) => {
    setRatioHeight(pageHeight / pageWidth)
  }

  const PageCounterStateful = () =>
    numPages > 1 && (
      <div className='no-print'>
        <PageCounter
          pageNumber={pageNumber}
          onPageNumberChange={setPageNumber}
          numPages={numPages}
          file={file}
        />
      </div>
    )

  const getPageHeight = () => {
    if (!ratioHeight) {
      return
    }
    const { width: pageWidth } = pageRef?.current?.getBoundingClientRect() || {}
    if (pageWidth === undefined) {
      return width * ratioHeight
    }
    setPageHeight(pageWidth * ratioHeight)
    return pageWidth * ratioHeight
  }

  useLayoutEffect(() => {
    const newPageWidth = pageRef?.current?.getBoundingClientRect?.()?.width
    if (newPageWidth) {
      setPageWidth(pageWidth)
      setPageHeight(pageWidth * ratioHeight)
    }
  }, [pageRef, pageWidth, pageHeight, ratioHeight])

  return (
    <Document
      noData={<LoadingDocument height={pageHeight} />}
      loading={<LoadingDocument height={pageHeight} />}
      file={file}
      onLoadSuccess={onDocumentLoadSuccess}
    >
      <PageCounterStateful />
      <Paper square>
        <div ref={pageRef}>
          <Page
            loading={<LoadingPage height={pageHeight} />}
            pageNumber={pageNumber}
            width={width}
            onRenderSuccess={onPageRenderSuccess}
          />
        </div>
      </Paper>
      <PageCounterStateful />
    </Document>
  )
}

export default PDFViewer
